/**
 * PHARMAKON — Trésorerie · the pure model of M1-M3: the one movement form, its confirmation, the
 * operation drawer and the minimal « Mouvements » list (TWP-20).
 *
 * design/ux/02_comptes_caisses_mouvements.md §10-§12; 02_ARCHITECTURE §2.4 (posting rules), §9.1,
 * §9.3; 03_UX_DESIGN P-5 / P-10 / P-11. The server is the authority on every figure and every rule
 * (`POST /operations/apercu` answers the « Après » of each side, the field refusals verbatim, the
 * permission verdict of « Confirmer maintenant », the nature default and the résumé): this module
 * only decides WHAT the form holds and sends — the sides a type requires, what a type switch keeps or
 * announces, the accounts a picker may offer under the pair matrix the référentiel serves, the modes
 * the chosen accounts admit, the request bodies, the field a refusal belongs to, the gestures a role
 * sees. It never adds, subtracts or rounds an amount: money stays the server's decimal strings, only
 * compared (zero / sign) and passed through. No DOM, no copy (the messages config words it).
 */
import { compareDecimal, toDecimalString } from "@/lib/number-format.js";

/** `TypeOperationTresorerie` (Prisma) — the keys the référentiel labels. */
export const TYPES_OPERATION = Object.freeze({ ENCAISSEMENT: "ENCAISSEMENT", DECAISSEMENT: "DECAISSEMENT", TRANSFERT: "TRANSFERT" });
export const TYPES_OPERATION_ORDRE = Object.freeze([TYPES_OPERATION.ENCAISSEMENT, TYPES_OPERATION.DECAISSEMENT, TYPES_OPERATION.TRANSFERT]);
/** `StatutOperationTresorerie` (Prisma). */
export const STATUTS_OPERATION = Object.freeze({ BROUILLON: "BROUILLON", CONFIRME: "CONFIRME", ANNULE: "ANNULE" });
/** E / D / T on the Type control (UX 02 §10.10) — bare letters, never inside a text field. */
export const LETTRES_TYPE = Object.freeze({ e: TYPES_OPERATION.ENCAISSEMENT, d: TYPES_OPERATION.DECAISSEMENT, t: TYPES_OPERATION.TRANSFERT });
/** The two sides of an operation (header columns, 02_ARCHITECTURE §2.2). */
export const COTES = Object.freeze({ SOURCE: "source_emplacement_id", DESTINATION: "destination_emplacement_id" });
/** The pair-matrix values the référentiel serves (`matrice_paires`, TD-145). */
export const PAIRES = Object.freeze({ OUI: "OUI", ENFANT: "ENFANT", PARENT: "PARENT", NON: "NON" });
/** Account types the model names (Prisma `TypeEmplacementFonds`). */
const T_BANQUE = "BANQUE";
const T_VIRTUEL = "VIRTUEL";
/** Mode keys the defaults name (Prisma `ModeReglement`); the list itself is served by the référentiel. */
const M_ESPECES = "ESPECES";
const M_VIREMENT = "VIREMENT";
/** `NatureFluxTresorerie.INTERNE` — « mouvement interne » (TD-124). */
export const NATURE_INTERNE = "INTERNE";

// ─── Posting rules (02_ARCHITECTURE §2.4) ───────────────────────────────────────────────────────────

/**
 * Which side a type requires: an encaissement goes TO an officine account (its source is optional —
 * « Hors trésorerie »), a décaissement comes FROM one, a transfer needs both.
 * @returns {{ source: boolean, destination: boolean }} true = required
 */
export function cotesRequis(type) {
  switch (type) {
    case TYPES_OPERATION.ENCAISSEMENT:
      return { source: false, destination: true };
    case TYPES_OPERATION.DECAISSEMENT:
      return { source: true, destination: false };
    case TYPES_OPERATION.TRANSFERT:
      return { source: true, destination: true };
    default:
      return { source: false, destination: false };
  }
}
const cleCote = (champ) => (champ === COTES.SOURCE ? "source" : "destination");
const autreCote = (champ) => (champ === COTES.SOURCE ? COTES.DESTINATION : COTES.SOURCE);

// ─── Dates (the officine's day, TD-68) ──────────────────────────────────────────────────────────────

/** « YYYY-MM-DD » of an instant in the officine's time zone (the référentiel's `fuseau`). */
export function jourOfficine(instant, fuseau) {
  if (!instant) return null;
  const d = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: fuseau || undefined, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/** Whole days from `jour` to `reference` (both « YYYY-MM-DD »): > 0 = `jour` is later. */
export function joursEntre(reference, jour) {
  if (!reference || !jour) return null;
  const a = Date.UTC(+reference.slice(0, 4), +reference.slice(5, 7) - 1, +reference.slice(8, 10));
  const b = Date.UTC(+jour.slice(0, 4), +jour.slice(5, 7) - 1, +jour.slice(8, 10));
  return Math.round((b - a) / 86_400_000);
}

/**
 * E-6 DueChip — the state of a Brouillon's announced date (`date_echeance`, else `date_prevue`) against
 * the officine's today: « aujourd'hui », « dans n jours », « en retard de n j ». Null when no date.
 * @returns {{ cle: "EN_RETARD"|"AUJOURDHUI"|"A_VENIR", jours: number } | null}
 */
export function etatEcheance(jourEcheance, aujourdhui) {
  const n = joursEntre(aujourdhui, jourEcheance);
  if (n == null) return null;
  if (n < 0) return { cle: "EN_RETARD", jours: -n };
  if (n === 0) return { cle: "AUJOURDHUI", jours: 0 };
  return { cle: "A_VENIR", jours: n };
}

// ─── Presets (UX 02 §10.2 — the form never forks) ───────────────────────────────────────────────────

/**
 * The entry points of the one form. A preset fixes what its entry point owns: a type (`type`), the
 * fields shown read-only with « Défini par … » (`verrous`), the default of « Confirmer maintenant »
 * (`confirmer`: false for a preset born Brouillon), the presentation (`presentation`) and, for
 * Alt+V, the destinations offered (`destinations: "enfants"`). The submission goes through the host's
 * adapter (`OperationForm` `api`): the generic operations API by default; a document preset's host
 * supplies its own (settlement `/reglements`, charge `/charges/:id/payer`, comptoir
 * `/comptoirs/moi/operations`) — one form, one validation path, one copy.
 */
export const PRESETS_OPERATION = Object.freeze({
  LIBRE: Object.freeze({ id: "LIBRE", verrous: Object.freeze([]), confirmer: true, presentation: "drawer-lg" }),
  COMPTE: Object.freeze({ id: "COMPTE", verrous: Object.freeze([]), confirmer: true, presentation: "drawer-lg" }),
  TRANSFERT: Object.freeze({ id: "TRANSFERT", type: TYPES_OPERATION.TRANSFERT, verrous: Object.freeze([]), confirmer: true, presentation: "drawer-lg" }),
  AFFECTATION: Object.freeze({
    id: "AFFECTATION",
    type: TYPES_OPERATION.TRANSFERT,
    verrous: Object.freeze(["type", COTES.SOURCE]),
    destinations: "enfants",
    confirmer: true,
    presentation: "drawer-lg",
  }),
  REGLEMENT: Object.freeze({ id: "REGLEMENT", type: TYPES_OPERATION.DECAISSEMENT, verrous: Object.freeze(["type", "motif_id", "tiers"]), confirmer: true, presentation: "drawer-lg", ventilation: true }),
  // TWP-27 (UX 03 §13.4) — a payment of ONE charge: the type, the motif (the charge's type, TD-74) and the
  // créancier are the charge's; it is born Confirmé (`brouillon: false` — no Ctrl+S draft, no
  // « Confirmer maintenant »); a payment has no announced échéance (`masques`).
  CHARGE: Object.freeze({
    id: "CHARGE",
    type: TYPES_OPERATION.DECAISSEMENT,
    verrous: Object.freeze(["type", "motif_id", "tiers", "document"]),
    masques: Object.freeze(["date_echeance"]),
    brouillon: false,
    confirmer: true,
    presentation: "drawer-lg",
  }),
  RECURRENT: Object.freeze({ id: "RECURRENT", verrous: Object.freeze(["type", "document"]), confirmer: false, presentation: "drawer-lg" }),
  FACTURATION: Object.freeze({ id: "FACTURATION", type: TYPES_OPERATION.ENCAISSEMENT, verrous: Object.freeze(["type", "tiers", "document"]), confirmer: true, presentation: "drawer-lg" }),
  OUVERTURE: Object.freeze({ id: "OUVERTURE", type: TYPES_OPERATION.ENCAISSEMENT, verrous: Object.freeze(["type", COTES.DESTINATION, "motif_id", "document"]), confirmer: true, presentation: "drawer-lg" }),
  COMPTOIR: Object.freeze({ id: "COMPTOIR", verrous: Object.freeze(["comptoir"]), confirmer: true, presentation: "drawer-md" }),
});

/** A preset by id (LIBRE when unknown). */
export const presetDe = (id) => PRESETS_OPERATION[id] ?? PRESETS_OPERATION.LIBRE;

/** Is a field locked by the preset (the comptoir preset locks the side its type implies)? */
export function estVerrouille(preset, champ, type) {
  const p = typeof preset === "string" ? presetDe(preset) : preset ?? PRESETS_OPERATION.LIBRE;
  if (p.verrous.includes(champ)) return true;
  if (p.verrous.includes("comptoir") && (champ === COTES.SOURCE || champ === COTES.DESTINATION)) {
    // The caissier's own comptoir: the side the type moves money out of (D, T) or into (E).
    return type === TYPES_OPERATION.ENCAISSEMENT ? champ === COTES.DESTINATION : champ === COTES.SOURCE;
  }
  return false;
}

/**
 * The empty form of an entry point.
 * @param {{ preset?: object, type?: string, compteId?: string|null, cote?: string|null, contexte?: object, aujourdhui: string }} p
 *   `compteId` (C1 / C2 F7, Alt+T, Alt+V): the account placed on the side the type implies;
 *   `contexte`: the document preset's values (montant, motif_id, tiers, document, dates…).
 */
export function valeursInitiales({ preset = PRESETS_OPERATION.LIBRE, type = null, compteId = null, contexte = {}, aujourdhui }) {
  const t = preset.type ?? type ?? contexte.type ?? TYPES_OPERATION.ENCAISSEMENT;
  const v = {
    type: t,
    [COTES.SOURCE]: contexte[COTES.SOURCE] ?? null,
    [COTES.DESTINATION]: contexte[COTES.DESTINATION] ?? null,
    montant: contexte.montant != null ? toDecimalString(contexte.montant) : null,
    mode: contexte.mode ?? null,
    modeChoisi: Boolean(contexte.mode),
    reference: contexte.reference ?? "",
    motif_id: contexte.motif_id ?? null,
    tiers: contexte.tiers ?? null,
    note: contexte.note ?? "",
    nature: null,
    date_operation: contexte.date_operation ?? aujourdhui,
    date_echeance: contexte.date_echeance ?? null,
    confirmer: contexte.confirmer ?? preset.confirmer !== false,
    date_confirmation: contexte.date_confirmation ?? aujourdhui,
    document: contexte.document ?? null,
  };
  if (compteId) {
    const req = cotesRequis(t);
    // The account goes on the side the type requires; a transfer « depuis ce compte » starts from it.
    const champ = t === TYPES_OPERATION.ENCAISSEMENT ? COTES.DESTINATION : req.source ? COTES.SOURCE : COTES.DESTINATION;
    v[champ] = compteId;
  }
  return v;
}

/**
 * The first field the focus lands on (UX 02 §10.10): « Type » unless the preset fixes it — or the
 * entry point already named it (« Nouvel encaissement », F3 › Transfert: the type stays changeable
 * but is not asked again) — else the first empty required field in reading order (usually « Montant »).
 */
export function champInitial(preset, valeurs, { typeDonne = false } = {}) {
  const p = preset ?? PRESETS_OPERATION.LIBRE;
  if (!typeDonne && !p.type && !p.verrous.includes("type")) return "type";
  const req = cotesRequis(valeurs.type);
  if (req.source && !valeurs[COTES.SOURCE] && !estVerrouille(p, COTES.SOURCE, valeurs.type)) return COTES.SOURCE;
  if (req.destination && !valeurs[COTES.DESTINATION] && !estVerrouille(p, COTES.DESTINATION, valeurs.type)) return COTES.DESTINATION;
  if (valeurs.montant == null) return "montant";
  if (!valeurs.motif_id && !p.verrous.includes("motif_id")) return "motif_id";
  return "montant";
}

// ─── Type switch, swap (UX 02 §10.4 — compatible values kept, the others announced) ─────────────────

/**
 * One switch of the Type control. Compatible values stay (a filled destination stays when E → T);
 * an incompatible one is removed or moved, and each automatic effect is returned as an announcement
 * (P-10 « nothing silent »):
 *   - T → E removes the source, T → D removes the destination (the sides those types do not require);
 *   - E → D / D → E with only the old required side filled moves that account to the new required
 *     side (it is the account of the movement); with both sides filled both stay (internal movement);
 *   - a motif whose `types_autorises` does not include the new type is removed;
 *   - a locked side never moves.
 * @returns {{ valeurs: object, annonces: Array<{ cle: string, compte?: string, cote?: string, motif?: string }> }}
 */
export function changerType(valeurs, type, { preset = PRESETS_OPERATION.LIBRE, comptes = [], motifs = [] } = {}) {
  if (!type || valeurs.type === type) return { valeurs, annonces: [] };
  const n = { ...valeurs, type };
  const annonces = [];
  const nom = (id) => comptes.find((c) => c.id === id)?.libelle ?? null;
  const libre = (champ) => !estVerrouille(preset, champ, type);
  const { source: src, destination: dst } = { source: valeurs[COTES.SOURCE], destination: valeurs[COTES.DESTINATION] };
  const ancien = valeurs.type;
  if (ancien === TYPES_OPERATION.TRANSFERT && type === TYPES_OPERATION.ENCAISSEMENT && src && libre(COTES.SOURCE)) {
    n[COTES.SOURCE] = null;
    annonces.push({ cle: "sourceRetiree" });
  } else if (ancien === TYPES_OPERATION.TRANSFERT && type === TYPES_OPERATION.DECAISSEMENT && dst && libre(COTES.DESTINATION)) {
    n[COTES.DESTINATION] = null;
    annonces.push({ cle: "destinationRetiree" });
  } else if (ancien === TYPES_OPERATION.ENCAISSEMENT && type === TYPES_OPERATION.DECAISSEMENT && dst && !src && libre(COTES.SOURCE) && libre(COTES.DESTINATION)) {
    n[COTES.SOURCE] = dst;
    n[COTES.DESTINATION] = null;
    annonces.push({ cle: "compteDeplace", compte: nom(dst), cote: "source" });
  } else if (ancien === TYPES_OPERATION.DECAISSEMENT && type === TYPES_OPERATION.ENCAISSEMENT && src && !dst && libre(COTES.SOURCE) && libre(COTES.DESTINATION)) {
    n[COTES.DESTINATION] = src;
    n[COTES.SOURCE] = null;
    annonces.push({ cle: "compteDeplace", compte: nom(src), cote: "destination" });
  }
  if (n.motif_id) {
    const m = motifs.find((x) => x.id === n.motif_id);
    if (m && !(m.types_autorises ?? []).includes(type)) {
      n.motif_id = null;
      annonces.push({ cle: "motifRetire", motif: m.libelle });
    }
  }
  return { valeurs: n, annonces };
}

/** F8 « Inverser source et destination » (transfer only; never across a locked side). */
export function inverserCotes(valeurs, { preset = PRESETS_OPERATION.LIBRE } = {}) {
  if (valeurs.type !== TYPES_OPERATION.TRANSFERT) return null;
  if (estVerrouille(preset, COTES.SOURCE, valeurs.type) || estVerrouille(preset, COTES.DESTINATION, valeurs.type)) return null;
  return { ...valeurs, [COTES.SOURCE]: valeurs[COTES.DESTINATION], [COTES.DESTINATION]: valeurs[COTES.SOURCE] };
}

// ─── Accounts (E-2 ComptePicker — UX 02 §10.5, TD-145) ──────────────────────────────────────────────

/**
 * Is the pair (source → destination) admitted by the structural matrix the référentiel serves
 * (`matrice_paires`): OUI; ENFANT = the destination is a virtual caisse of the source (affectation);
 * PARENT = the destination is the source's parent (désaffectation); NON never.
 */
export function paireAdmise(matrice, source, destination) {
  if (!source || !destination) return true;
  const regle = matrice?.[source.type]?.[destination.type];
  if (regle === PAIRES.OUI) return true;
  if (regle === PAIRES.ENFANT) return destination.parent_id === source.id;
  if (regle === PAIRES.PARENT) return source.parent_id === destination.id;
  return false;
}

/**
 * The accounts a side's picker offers: the active accounts of the officine, minus the other side's
 * account (source ≠ destination), minus the cells the matrix forbids with the other side chosen; a
 * preset restricted to « enfants » (Alt+V) offers only the virtual caisses of the source. `filtres`
 * says the list is shorter for a reason the footer words (TD-145).
 * @param {{ comptes: object[], champ: string, valeurs: object, matrice: object, restriction?: string|null }} p
 * @returns {{ comptes: object[], filtres: boolean, restriction: string|null }}
 */
export function comptesPossibles({ comptes, champ, valeurs, matrice, restriction = null }) {
  const autreId = valeurs[autreCote(champ)];
  const autre = autreId ? (comptes ?? []).find((c) => c.id === autreId) ?? null : null;
  const actifs = (comptes ?? []).filter((c) => c.is_actif !== false && c.id !== autreId);
  let liste = actifs;
  if (restriction === "enfants" && champ === COTES.DESTINATION) {
    liste = autre ? actifs.filter((c) => c.parent_id === autre.id) : [];
    return { comptes: liste, filtres: false, restriction: "enfants" };
  }
  if (autre) {
    liste = actifs.filter((c) => (champ === COTES.DESTINATION ? paireAdmise(matrice, autre, c) : paireAdmise(matrice, c, autre)));
  }
  const filtres = liste.length < actifs.length && actifs.some((c) => c.type === T_VIRTUEL && !liste.includes(c));
  return { comptes: liste, filtres, restriction: null };
}

/** The hierarchy path of an account (« Caisse principale › Cosmétiques » for a virtual caisse). */
export function cheminCompte(compte, comptes) {
  if (!compte) return null;
  if (!compte.parent_id) return compte.libelle;
  const parent = (comptes ?? []).find((c) => c.id === compte.parent_id);
  return parent ? `${parent.libelle} › ${compte.libelle}` : compte.libelle;
}

// ─── Modes (TD-10, TD-212) ──────────────────────────────────────────────────────────────────────────

/**
 * The payment modes the chosen accounts admit, in the référentiel's order: with the strict control on
 * (`controle_mode_compte_strict`), a mode admitted by ONE of the operation's accounts (TD-212); no
 * account chosen (or the control off: the server only warns) → every mode.
 */
export function modesAdmis(source, destination, referentiel) {
  const tous = (referentiel?.modes_reglement ?? []).map((m) => m.cle);
  const strict = referentiel?.parametres?.controle_mode_compte_strict !== false;
  const cotes = [source, destination].filter(Boolean);
  if (!strict || cotes.length === 0) return tous;
  const parType = referentiel?.modes_par_type_compte ?? {};
  const admis = new Set(cotes.flatMap((c) => parType[c.type] ?? []));
  return tous.filter((m) => admis.has(m));
}

/** ESPECES when the accounts are cash, VIREMENT when one is a bank (UX 02 §10.6). */
export function modeParDefaut(source, destination) {
  return [source, destination].some((c) => c?.type === T_BANQUE) ? M_VIREMENT : M_ESPECES;
}

/**
 * Keep the mode coherent with the accounts: an empty mode takes the default silently (it was never
 * typed); a mode the user did not choose follows the default; a chosen mode the accounts no longer
 * admit is replaced — and that replacement is announced.
 * @returns {{ valeurs: object, annonce: { cle: "modeChange"|"modeSuit", avant: string, apres: string } | null }}
 */
export function ajusterMode(valeurs, { source, destination, referentiel }) {
  const admis = modesAdmis(source, destination, referentiel);
  const defaut = admis.includes(modeParDefaut(source, destination)) ? modeParDefaut(source, destination) : admis[0] ?? null;
  if (!valeurs.mode) return { valeurs: defaut ? { ...valeurs, mode: defaut } : valeurs, annonce: null };
  if (admis.includes(valeurs.mode) && (valeurs.modeChoisi || valeurs.mode === defaut)) return { valeurs, annonce: null };
  if (!defaut || defaut === valeurs.mode) return { valeurs, annonce: null };
  // Not admitted any more → « remplacé » ; admitted but never chosen → it follows the accounts' default.
  const cle = admis.includes(valeurs.mode) ? "modeSuit" : "modeChange";
  return { valeurs: { ...valeurs, mode: defaut, modeChoisi: false }, annonce: { cle, avant: valeurs.mode, apres: defaut } };
}

/** Does this mode require a reference (chèque, virement — refonte D11, `modes_reglement[].reference_requise`)? */
export function referenceRequise(mode, referentiel) {
  return Boolean((referentiel?.modes_reglement ?? []).find((m) => m.cle === mode)?.reference_requise);
}

// ─── Motifs (TD-127 groups, TAB-13) ─────────────────────────────────────────────────────────────────

/**
 * The motifs the form offers for a type: active, not a group heading, `types_autorises` holding the
 * type; each carries its group's label (the Combobox groups them in the référentiel's order).
 * @returns {Array<{ value: string, label: string, group?: string, meta?: string, motif: object }>}
 */
export function optionsMotifs(type, referentiel) {
  const motifs = referentiel?.motifs ?? [];
  const groupes = new Map(motifs.filter((m) => m.est_groupe).map((g) => [g.id, g.libelle]));
  return motifs
    .filter((m) => !m.est_groupe && m.is_actif !== false && (m.types_autorises ?? []).includes(type))
    .map((m) => ({ value: m.id, label: m.libelle, ...(m.groupe_id && groupes.get(m.groupe_id) ? { group: groupes.get(m.groupe_id) } : {}), motif: m }));
}

// ─── Request bodies (TA-03: decimal strings both ways) ──────────────────────────────────────────────

const texteOuNull = (v) => (v == null || String(v).trim() === "" ? null : String(v).trim());

/**
 * The statut the operation is asked to be born with: CONFIRME when « Confirmer maintenant » is ticked
 * and the server's verdict allows it, else BROUILLON.
 */
export function statutCible(valeurs, confirmationAutorisee = true) {
  return valeurs.confirmer && confirmationAutorisee !== false ? STATUTS_OPERATION.CONFIRME : STATUTS_OPERATION.BROUILLON;
}

/**
 * The body of `POST /operations/apercu` for a creation: every field typed so far (the server names the
 * missing ones). A Brouillon carries no date de confirmation (the engine refuses one).
 */
export function corpsApercu(valeurs, { confirmationAutorisee = true, natureModifiee = false } = {}) {
  const statut = statutCible(valeurs, confirmationAutorisee);
  const corps = {
    type: valeurs.type,
    statut_cible: statut,
    source_emplacement_id: valeurs[COTES.SOURCE] ?? null,
    destination_emplacement_id: valeurs[COTES.DESTINATION] ?? null,
  };
  if (valeurs.montant != null) corps.montant = toDecimalString(valeurs.montant);
  if (valeurs.mode) corps.mode = valeurs.mode;
  corps.reference = texteOuNull(valeurs.reference);
  if (valeurs.motif_id) corps.motif_id = valeurs.motif_id;
  corps.tiers_id = valeurs.tiers?.id ?? null;
  corps.note = texteOuNull(valeurs.note);
  if (natureModifiee && valeurs.nature) corps.nature = valeurs.nature;
  if (valeurs.date_operation) corps.date_operation = valeurs.date_operation;
  corps.date_echeance = valeurs.date_echeance || null;
  if (statut === STATUTS_OPERATION.CONFIRME && valeurs.date_confirmation) corps.date_confirmation = valeurs.date_confirmation;
  return corps;
}

/** The body of `POST /operations` (the preview's fields + the form's idempotency key, TA-16). */
export function corpsCreation(valeurs, { statut, idempotencyKey, natureModifiee = false }) {
  const corps = corpsApercu({ ...valeurs, confirmer: statut === STATUTS_OPERATION.CONFIRME }, { natureModifiee });
  corps.statut_cible = statut;
  if (statut !== STATUTS_OPERATION.CONFIRME) delete corps.date_confirmation;
  if (idempotencyKey) corps.idempotency_key = idempotencyKey;
  return corps;
}

/** A fresh client key for one opening of the form (a retry reuses it: a replay, never a double). */
export function nouvelleCleIdempotence(prefixe = "treso-form") {
  const id = globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefixe}:${id}`;
}

// ─── Direct edit (UX 02 §12.2, 02_ARCHITECTURE §2.6) ────────────────────────────────────────────────

/** The form of an existing operation (modification mode). Dates are officine days. */
export function valeursDeLOperation(op, fuseau) {
  return {
    type: op.type,
    [COTES.SOURCE]: op.source?.id ?? null,
    [COTES.DESTINATION]: op.destination?.id ?? null,
    montant: op.montant ?? null,
    mode: op.mode ?? null,
    modeChoisi: true,
    reference: op.reference ?? "",
    motif_id: op.motif?.id ?? null,
    tiers: op.tiers ? { id: op.tiers.id, nom: op.tiers.nom, code: op.tiers.code ?? null, type: op.tiers.type ?? null } : null,
    note: op.note ?? "",
    nature: op.nature ?? null,
    date_operation: jourOfficine(op.date_operation, fuseau),
    date_echeance: jourOfficine(op.date_echeance, fuseau),
    confirmer: op.statut === STATUTS_OPERATION.CONFIRME,
    date_confirmation: jourOfficine(op.date_confirmation, fuseau),
    document: null,
  };
}

const memeMontant = (a, b) => {
  const x = toDecimalString(a);
  const y = toDecimalString(b);
  if (x == null || y == null) return x == null && y == null;
  return compareDecimal(x, y) === 0;
};
const memeTexte = (a, b) => (texteOuNull(a) ?? null) === (texteOuNull(b) ?? null);

/** The editable fields of the form and how each is compared (the server's `CHAMPS_MODIFIABLES_OPERATION`). */
export const CHAMPS_EDITION = Object.freeze([
  "montant",
  COTES.SOURCE,
  COTES.DESTINATION,
  "date_confirmation",
  "date_operation",
  "date_echeance",
  "mode",
  "reference",
  "note",
  "motif_id",
  "nature",
  "tiers_id",
]);

/**
 * The `changements` of `PATCH /operations/:id`: only the fields whose value differs from the version
 * the form started from (`base`, `valeursDeLOperation`). A Brouillon's date de confirmation is not
 * an editable value (it is set by the confirmation).
 */
export function changementsModification(valeurs, base, { statut } = {}) {
  const c = {};
  if (!memeMontant(valeurs.montant, base.montant)) c.montant = toDecimalString(valeurs.montant);
  for (const champ of [COTES.SOURCE, COTES.DESTINATION]) if ((valeurs[champ] ?? null) !== (base[champ] ?? null)) c[champ] = valeurs[champ] ?? null;
  if (statut === STATUTS_OPERATION.CONFIRME && (valeurs.date_confirmation ?? null) !== (base.date_confirmation ?? null) && valeurs.date_confirmation) {
    c.date_confirmation = valeurs.date_confirmation;
  }
  if ((valeurs.date_operation ?? null) !== (base.date_operation ?? null) && valeurs.date_operation) c.date_operation = valeurs.date_operation;
  if ((valeurs.date_echeance ?? null) !== (base.date_echeance ?? null)) c.date_echeance = valeurs.date_echeance || null;
  if ((valeurs.mode ?? null) !== (base.mode ?? null) && valeurs.mode) c.mode = valeurs.mode;
  if (!memeTexte(valeurs.reference, base.reference)) c.reference = texteOuNull(valeurs.reference);
  if (!memeTexte(valeurs.note, base.note)) c.note = texteOuNull(valeurs.note);
  if ((valeurs.motif_id ?? null) !== (base.motif_id ?? null) && valeurs.motif_id) c.motif_id = valeurs.motif_id;
  if ((valeurs.nature ?? null) !== (base.nature ?? null) && valeurs.nature) c.nature = valeurs.nature;
  if ((valeurs.tiers?.id ?? null) !== (base.tiers?.id ?? null)) c.tiers_id = valeurs.tiers?.id ?? null;
  return c;
}

/** The fields whose value differs from the base (the « Modifié » style, UX 02 §12.2). */
export function champsModifies(valeurs, base, opts) {
  const c = changementsModification(valeurs, base, opts);
  return new Set(Object.keys(c).map((k) => (k === "tiers_id" ? "tiers" : k)));
}

/** Restore one field to the base value (Ctrl+Retour arrière on a changed field). */
export function retablirChamp(valeurs, base, champ) {
  if (!(champ in base)) return valeurs;
  return { ...valeurs, [champ]: base[champ], ...(champ === "mode" ? { modeChoisi: true } : {}) };
}

/**
 * The 409 « modifiée pendant votre saisie » banner (UX 02 §10.9): the fields another user changed
 * between the version the form started from and the fresh one — the user's typed values survive.
 * @returns {string[]} field keys (form keys)
 */
export function champsChangesEntre(base, fraiche) {
  return [...champsModifies(fraiche, base, { statut: STATUTS_OPERATION.CONFIRME })];
}

// ─── Refusals and the preview (RC-64: the server's sentence verbatim, on its field) ─────────────────

/** Server field names → the form's field keys (a zod path `body.changements.montant` included). */
export function champDuServeur(champ) {
  if (!champ) return null;
  const c = String(champ).replace(/^(body|query|params)\./, "").replace(/^changements\./, "");
  if (c === "tiers_id") return "tiers";
  return c;
}

/**
 * Split the preview's `erreurs` (or a refused write's body) into per-field messages and the general
 * ones the summary at the top reads.
 * @param {{ erreurs?: Array<{champ: string|null, message: string}> } | { message?: string, details?: {champ?: string}, errors?: Array<{field: string, message: string}> } | null} source
 * @returns {{ parChamp: Record<string, string>, generales: string[] }}
 */
export function erreursParChamp(source) {
  const parChamp = {};
  const generales = [];
  if (!source) return { parChamp, generales };
  const poser = (champ, message) => {
    const c = champDuServeur(champ);
    if (!message) return;
    if (c && !parChamp[c]) parChamp[c] = message;
    else if (!c && !generales.includes(message)) generales.push(message);
  };
  if (Array.isArray(source.erreurs)) {
    for (const e of source.erreurs) poser(e.champ, e.message);
    return { parChamp, generales };
  }
  if (Array.isArray(source.errors) && source.errors.length) {
    for (const e of source.errors) poser(e.field, e.message);
    return { parChamp, generales };
  }
  poser(source.details?.champ ?? null, source.message);
  return { parChamp, generales };
}

/** The preview's per-side line, as the band reads it (null when the side is empty or unknown). */
export function ligneApercu(apercu, cote) {
  return apercu?.comptes?.[cleCote(cote)] ?? null;
}

/**
 * The gesture of a drawer action from the server's verdict (`actions.<geste>` of `GET /operations/:id`,
 * RC-49): `masque` when the role does not hold it (never rendered — P-11, DD-03), `desactive` with the
 * reason when the state forbids it now, `actif` otherwise.
 */
export function etatGeste(verdict, codeInterdit = "FORBIDDEN") {
  if (!verdict) return { etat: "masque", raison: null };
  if (verdict.autorise) return { etat: "actif", raison: null };
  if (verdict.code === codeInterdit) return { etat: "masque", raison: null };
  return { etat: "desactive", raison: verdict.raison ?? null };
}

/** `OrigineMouvementFonds` of a supplier settlement (its drawer opens a « Ventilation » tab). */
export const ORIGINE_REGLEMENT = "REGLEMENT_ACHAT";
/** A supplier settlement: edited, confirmed and cancelled through its owner's words and form (UX 03 §7). */
export const estReglement = (op) => op?.origine === ORIGINE_REGLEMENT;

/**
 * The tabs of the operation drawer (UX 02 §12.1, UX 03 §11.3), in order: « Détail », « Ventilation »
 * (a supplier settlement only), « Document source » (always — a manual operation says it has none),
 * « Historique », « Écritures » (only when the server sent the legs: the owner's, 03_UX §12.3).
 */
export function ongletsOperation(op) {
  if (!op) return ["detail"];
  const out = ["detail"];
  if (op.origine === ORIGINE_REGLEMENT || (op.allocations?.length ?? 0) > 0) out.push("ventilation");
  out.push("document", "historique");
  if (Array.isArray(op.ecritures)) out.push("ecritures");
  return out;
}

/**
 * The rows of the A4 pièce (UX 02 §12.1 « Imprimer la pièce »): the side money leaves, the side it
 * enters; an absent side reads « Hors trésorerie » (with the tiers). The amount is the server's string
 * (null when masked for the role). `mots` = the PIECE copy.
 */
export function lignesPiece(op, mots) {
  const montant = op.montant_masque ? null : op.montant;
  const hors = op.tiers ? `${mots.horsTresorerie} (${op.tiers.nom})` : mots.horsTresorerie;
  return [
    { id: "sort", cote: mots.sorties, libelle: op.source?.libelle ?? hors, montant },
    { id: "entre", cote: mots.entrees, libelle: op.destination?.libelle ?? hors, montant },
  ];
}

/** Both sides are officine accounts on an encaissement / décaissement: « Mouvement interne » (TD-124). */
export function estMouvementInterne({ type, source, destination, nature }) {
  if (nature === NATURE_INTERNE && type !== TYPES_OPERATION.TRANSFERT) return true;
  return type !== TYPES_OPERATION.TRANSFERT && Boolean(source) && Boolean(destination);
}

/** « Comptoir 2 ⟶ Caisse centrale », « Hors trésorerie » for an absent side. */
export function texteFlux(source, destination, horsTresorerie) {
  return `${source?.libelle ?? horsTresorerie} ⟶ ${destination?.libelle ?? horsTresorerie}`;
}

// ─── The minimal journal (until TWP-29) ─────────────────────────────────────────────────────────────

/** The direction of a journal row the server sends (`sens`, UX 03 §9.3 line 2). */
export const SENS = Object.freeze({ ENTREE: "ENTREE", SORTIE: "SORTIE", INTERNE: "INTERNE" });

/** The query parameters of the « Mouvements » leaf: the view, the form a palette verb opens. */
export const PARAM_VUE = "vue";
export const PARAM_NOUVEAU = "nouveau";
/**
 * TWP-23 — `?motif=<id>`: the journal filtered on one motif (the « Utilisé » link of Paramètres ›
 * Motifs, UX 02 §15.4); the server applies it (`GET /operations?motif=`, TWP-30 filter). A value
 * that is not an id is ignored (never sent).
 */
export const PARAM_MOTIF = "motif";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const motifDuParametre = (v) => (v && UUID.test(String(v)) ? String(v) : null);
/** The rail badge of « Mouvements » (`navigation.config.js` `badge`): Brouillons à confirmer (UX 01 §2.2). */
export const BADGE_BROUILLONS = "tresorerie.brouillons";

/** The views the journal API serves (`vue`, 03_UX_DESIGN §2.2) that this leaf shows. */
export const VUES_MOUVEMENTS = Object.freeze(["tous", "encaissements", "decaissements", "transferts", "a-confirmer", "annulees"]);
export const VUE_MOUVEMENTS_DEFAUT = "tous";
export const VUE_A_CONFIRMER = "a-confirmer";
export const vueValide = (v) => (VUES_MOUVEMENTS.includes(v) ? v : VUE_MOUVEMENTS_DEFAUT);

/** The `?nouveau=` of a palette verb / an entry link (« Nouvel encaissement ») → the type, or null. */
export function typeDuParametre(v) {
  const t = String(v ?? "").toUpperCase();
  return TYPES_OPERATION_ORDRE.includes(t) ? t : null;
}

/** The previous / next operation of a list (Ctrl+↑ / Ctrl+↓ in the drawer), or null at an end. */
export function operationVoisine(lignes, id, sens) {
  const i = (lignes ?? []).findIndex((l) => l.id === id);
  if (i < 0) return null;
  return lignes[i + sens]?.id ?? null;
}

/** The Brouillons of a selection (bulk confirmation only acts on them, TD-126). */
export function brouillonsSelectionnes(lignes, ids) {
  const set = new Set(ids ?? []);
  return (lignes ?? []).filter((l) => set.has(l.id) && l.statut === STATUTS_OPERATION.BROUILLON);
}

/** Is the amount a positive decimal (the server stays authoritative; this only gates the empty field)? */
export function montantSaisi(v) {
  const d = toDecimalString(v);
  return d != null && compareDecimal(d, "0") > 0;
}

/**
 * « Reprendre mes valeurs sur la nouvelle version » (UX 02 §10.9): the fresh version of the operation
 * with the user's own edits replayed on it — a field the user changed keeps the typed value, every
 * other field takes the fresh one (another user's change is never reverted silently).
 */
export function rebaserValeurs(valeurs, ancienneBase, nouvelleBase) {
  const modifies = champsModifies(valeurs, ancienneBase, { statut: STATUTS_OPERATION.CONFIRME });
  const out = { ...nouvelleBase };
  for (const k of Object.keys(valeurs)) {
    const champ = k === "tiers" ? "tiers" : k;
    if (modifies.has(champ)) out[k] = valeurs[k];
  }
  out.modeChoisi = true;
  return out;
}
